import { useState, useMemo } from "react";

import { createSmsSimulatorRulesTableColumns } from "containers/sms-simulator/sms-simulator-rules/sms-simulator-rules-table-columns";

import {
  useCreateSmsSimulatorRule,
  useDeleteSmsSimulatorRule,
  useReorderSmsSimulatorRules,
  useSmsSimulatorRules as useSmsSimulatorRulesQuery,
  useUpdateSmsSimulatorRuleDescription,
} from "hooks/api/use-sms-simulator-rules";

import { SmsSimulatorRuleAttributes } from "containers/sms-simulator/sms-simulator.types";

export const useSmsSimulatorRules = () => {
  const { data: rules = [], isLoading: isRulesLoading } = useSmsSimulatorRulesQuery();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<SmsSimulatorRuleAttributes | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const createRule = useCreateSmsSimulatorRule();
  const updateRuleDescription = useUpdateSmsSimulatorRuleDescription();
  const reorderRules = useReorderSmsSimulatorRules();
  const deleteRule = useDeleteSmsSimulatorRule();

  const handleEdit = (id: string) => {
    const ruleToEdit = rules.find((rule) => rule.id === id);
    if (ruleToEdit) {
      setSelectedRule(ruleToEdit);
      setIsModalOpen(true);
    } else {
      console.error("Rule not found in frontend state for ID:", id);
    }
  };

  const handleDelete = (id: string) => {
    const ruleToDelete = rules.find((rule) => rule.id === id);
    if (ruleToDelete) {
      setSelectedRule(ruleToDelete);
      setIsDeleteModalOpen(true);
    }
  };

  const handleReorder = (newRules: SmsSimulatorRuleAttributes[]) => {
    reorderRules.mutate(newRules);
  };

  const handleConfirmDelete = async () => {
    if (!selectedRule) {
      return;
    }

    try {
      await deleteRule.mutateAsync(selectedRule.id);
      setSelectedRule(null);
      setIsDeleteModalOpen(false);
    } catch {
      // Error toast handled in the mutation; keep the modal open for retry.
    }
  };

  const handleSubmitRule = async (ruleText: string, ruleId?: string) => {
    if (ruleId) {
      await updateRuleDescription.mutateAsync({ id: ruleId, request: { description: ruleText } });
    } else {
      await createRule.mutateAsync({ description: ruleText });
    }

    setSelectedRule(null);
  };

  const handleOpenAddModal = () => {
    setSelectedRule(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRule(null);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedRule(null);
  };

  const columns = useMemo(
    () =>
      createSmsSimulatorRulesTableColumns({
        onEdit: handleEdit,
        onDelete: handleDelete,
      }),
    [handleEdit, handleDelete],
  );

  return {
    // State
    rules,
    isRulesLoading,
    isModalOpen,
    selectedRule,
    isDeleteModalOpen,
    columns,

    // Handlers
    handleEdit,
    handleDelete,
    handleReorder,
    handleConfirmDelete,
    handleSubmitRule,
    handleOpenAddModal,
    handleCloseModal,
    handleCloseDeleteModal,
  };
};
